import React, { useImperativeHandle, useLayoutEffect, useMemo, useRef } from "react";

import { animated, type Interpolation, type SpringValue } from "react-spring";

import { useTimelineDataStore, useTimelineStore } from "../store/timeline-context";
import { formatPointerTime } from "../utils/ruler";
import { placeTimeChip, TIME_CHIP_OFFSET_Y } from "./timeChip";

type Props = {
  pixelsPerSecond: number;
  scrollLeft: number;
  viewportWidth: number;
  scrubBarHeight: number;
  /** Animated height of the bottom timeline region, as for the playhead. */
  bottomHeight: SpringValue<number> | Interpolation<number>;
  isMinimized?: boolean;
  numOfRenderableRows: number;
};

export interface HoverIndicatorHandle {
  /** Pointer x within the canvas column, or `null` once it leaves. */
  setPointerX: (x: number | null) => void;
}

/**
 * A hairline under a mouse pointer over the timeline, with the time it points
 * at. Positioned imperatively like the playhead; hidden while scrubbing, when
 * the playhead's own bubble reads the time.
 */
export const HoverIndicator = React.forwardRef<HoverIndicatorHandle, Props>(
  (
    { pixelsPerSecond, scrollLeft, viewportWidth, scrubBarHeight, bottomHeight, isMinimized, numOfRenderableRows },
    ref,
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const chipRef = useRef<HTMLDivElement>(null);
    const pointerXRef = useRef<number | null>(null);
    const isScrubbing = useTimelineStore((state) => state.isScrubbing);
    const durationSeconds = useTimelineDataStore((state) => state.durationSeconds);

    const layoutRef = useRef({ pixelsPerSecond, scrollLeft, viewportWidth, durationSeconds, isScrubbing });
    layoutRef.current = { pixelsPerSecond, scrollLeft, viewportWidth, durationSeconds, isScrubbing };

    const update = () => {
      const wrapper = wrapperRef.current;
      const chip = chipRef.current;
      if (!wrapper || !chip) return;
      const { pixelsPerSecond, scrollLeft, viewportWidth, durationSeconds, isScrubbing } = layoutRef.current;
      const x = pointerXRef.current;
      const time = x === null ? NaN : (x + scrollLeft) / pixelsPerSecond;
      const visible = x !== null && !isScrubbing && x >= 0 && x <= viewportWidth && time <= durationSeconds;
      wrapper.style.visibility = visible ? "visible" : "hidden";
      if (!visible) return;

      wrapper.style.transform = `translateX(${Math.round(x)}px)`;
      placeTimeChip(chip, formatPointerTime(time, pixelsPerSecond, durationSeconds), x, viewportWidth);
    };

    useImperativeHandle(ref, () => ({
      setPointerX: (x) => {
        pointerXRef.current = x;
        update();
      },
    }));

    // Scrolling or zooming under a still pointer changes the time it points at.
    // `update` reads these values through `layoutRef`.
    useLayoutEffect(update, [pixelsPerSecond, scrollLeft, viewportWidth, durationSeconds, isScrubbing]);

    const lineHeight = useMemo(
      () => bottomHeight.to((h: number) => Math.max(0, scrubBarHeight + h)),
      [bottomHeight, scrubBarHeight],
    );

    return (
      <div
        aria-hidden
        className="pointer-events-none absolute"
        ref={wrapperRef}
        style={{ top: 0, left: 0, width: 1, zIndex: 9, visibility: "hidden" }}
      >
        <animated.div
          className="absolute left-0 top-0 w-px bg-white/35"
          style={{ height: !isMinimized && numOfRenderableRows > 0 ? lineHeight : scrubBarHeight }}
        />
        <div
          className="absolute whitespace-nowrap rounded border border-white/15 bg-black/80 px-1.5 py-0.5 text-[10px] leading-none text-white"
          ref={chipRef}
          style={{ top: -TIME_CHIP_OFFSET_Y, left: 0, fontVariantNumeric: "tabular-nums" }}
        />
      </div>
    );
  },
);

HoverIndicator.displayName = "HoverIndicator";
